import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useLocale, useT } from "../../i18n";
import { api } from "../../lib/api";
import { fmt } from "../../lib/format";
import { useTaxonomies } from "../../lib/taxonomies";
import { EMPTY_FILTERS, activeFilterCount, toApiParams } from "../../hooks/useInventory";
import { ChipGroup, DisplacementFields, MakeModelFields, MileageField, PriceFields, YearFields } from "./FilterControls";
import { EquipmentFilter } from "./EquipmentFilter";

const Block = ({ title, children, wide }) => (
  <section className={`border-b border-ink/10 py-6 ${wide ? "lg:col-span-2" : ""}`}>
    {title && <h3 className="micro-label mb-4 text-ink/80">{title}</h3>}
    {children}
  </section>
);

// Draft filters with a debounced live count from the facets endpoint; nothing changes the URL until Apply.
const useLiveFacets = (draft, open, lang) => {
  const [live, setLive] = useState(null);
  const key = JSON.stringify(toApiParams(draft));
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => {
      api.get("/vehicles/facets", { params: { ...JSON.parse(key), lang } }).then((r) => setLive(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(timer);
  }, [key, open, lang]);
  return live;
};

export const AdvancedFilters = ({ open, onOpenChange, filters, setFilters, facets }) => {
  const t = useT();
  const lang = useLocale();
  const [tx] = useTaxonomies();
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);
  const live = useLiveFacets(draft, open, lang) || facets;
  const props = { value: draft, onChange: (patch) => setDraft((d) => ({ ...d, ...patch })), facets: live, idPrefix: "adv" };
  const chips = (label, field, list) => <ChipGroup label={label} field={field} options={live?.[list]} {...props} />;
  // every standard colour is always listed; ones with no current results are greyed out, not hidden
  const colorCounts = Object.fromEntries((live?.exterior_colors || []).map((o) => [o.value, o.count]));
  const colorOptions = (tx?.colors || []).map((c) => ({ value: c.key, label: c.label, count: colorCounts[c.key] || 0 }));
  const apply = () => { setFilters(draft); onOpenChange(false); };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] bg-black/50" />
        <Dialog.Content aria-describedby={undefined} data-testid="advanced-filters"
          className="fixed inset-0 z-[61] flex flex-col bg-white text-ink outline-none lg:inset-auto lg:left-1/2 lg:top-1/2 lg:h-[88vh] lg:w-[min(1120px,94vw)] lg:-translate-x-1/2 lg:-translate-y-1/2">
          <div className="flex items-center justify-between border-b border-ink/10 px-5 py-3 lg:px-8">
            <Dialog.Title className="font-display text-2xl">{t("filters.moreFilters")}</Dialog.Title>
            <span className="micro-label ml-auto mr-4 tabular-nums text-ink/50" data-testid="adv-selected-count">{fmt(t("filters.selectedCount"), { n: activeFilterCount(draft) })}</span>
            <Dialog.Close aria-label={t("filters.close")} data-testid="adv-close" className="-mr-2 flex h-11 w-11 items-center justify-center"><X size={18} strokeWidth={1.25} /></Dialog.Close>
          </div>
          <div className="grid flex-1 content-start overflow-y-auto overscroll-contain px-5 lg:grid-cols-2 lg:gap-x-12 lg:px-8">
            <Block><MakeModelFields {...props} /></Block>
            <Block title={t("filters.year")}><YearFields {...props} /></Block>
            <Block title={t("filters.price")}><PriceFields {...props} /></Block>
            <Block><MileageField {...props} /></Block>
            <Block title={t("filters.displacement")}><DisplacementFields {...props} /></Block>
            <Block>{chips(t("filters.market"), "market", "markets")}</Block>
            <Block wide>{chips(t("filters.body"), "body_type", "body_types")}</Block>
            <Block>{chips(t("filters.fuel"), "fuel", "fuel_types")}</Block>
            <Block>{chips(t("filters.transmission"), "transmission", "transmissions")}</Block>
            <Block>{chips(t("filters.drivetrain"), "drivetrain", "drivetrains")}</Block>
            <Block>{chips(t("filters.aspiration"), "aspiration", "aspirations")}</Block>
            <Block wide><div data-testid="adv-colour-section"><ChipGroup label={t("filters.exteriorColor")} field="exterior_color" options={colorOptions} {...props} /></div></Block>
            <Block title={t("filters.equipment")} wide><EquipmentFilter {...props} groups={tx?.equipment_groups} /></Block>
          </div>
          <div className="grid grid-cols-[auto_1fr] gap-3 border-t border-ink/10 px-5 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 lg:px-8">
            <button type="button" onClick={() => setDraft({ ...EMPTY_FILTERS, sort: draft.sort, scope: draft.scope })} data-testid="adv-clear" className="btn btn-ghost-dark">{t("filters.clearAll")}</button>
            <button type="button" onClick={apply} data-testid="adv-apply" className="btn btn-dark">
              {fmt(t("filters.showResults"), { n: live?.total ?? "…" })}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
